<script setup lang="ts">
import { nuxtUiMerge } from '~/utils/tw-utils'
import type { HeadingType, UIProp } from '~/types/elementsUi'

interface VSectionProps {
  title?: string
  titleId?: string
  description?: string
  hideDivider?: boolean
  narration?: boolean
  baseKey?: string
  titleType?: HeadingType
  ui?: UIProp<'section' | 'title' | 'description'>
}

const props = defineProps<VSectionProps>()

const route = useRoute()
const { t, te } = useI18n(props.baseKey)

const section = useTemplateRef<HTMLElement>('section')
const { onClick, buttonIcon, isSupported, ariaLabel } = useNarration(section)

const computedUI = computed(() =>
  nuxtUiMerge(props.ui, {
    title: tw`not-last:mb-0`,
    description: tw`prose max-w-section empty:hidden`
  })
)

const showNarration = computed(() => {
  if (!isSupported.value) {
    return false
  }

  return props.narration || 'tts' in route.query
})
</script>

<template>
  <section ref="section" class="section" :class="computedUI?.section">
    <template v-if="title || $slots['title']">
      <div class="mb-6 flex items-center justify-between">
        <component
          :is="titleType ?? 'h2'"
          :id="titleId"
          class="section-title"
          :class="computedUI?.title"
        >
          <slot name="title">
            {{ title && te(title) ? t(title) : title }}
          </slot>
        </component>
        <VButton
          v-if="showNarration"
          :icon-only="buttonIcon"
          :aria-label="ariaLabel"
          aria-hidden="true"
          class="h-9 w-9"
          @click="onClick"
        />
      </div>
      <VDivider v-if="!hideDivider" class="mb-6 hidden lg:block" />
    </template>

    <slot name="content">
      <div :class="computedUI?.description">
        <slot>
          <p v-if="description">
            {{ te(description) ? t(description) : description }}
          </p>
        </slot>
      </div>
    </slot>

    <slot name="after" />
  </section>
</template>

<style scoped></style>
